// Module 4.1 — Kill Sheet compute orchestrator.
//
// Pure function: given a KillSheet record (specifically its `inputs`),
// returns a fresh `outputs` object. Never mutates the caller's record.
//
// This is what the editor calls on every input change for live recompute,
// and what storage calls on every persist to refresh the audit snapshot.

import { kmwPpg, icpPsi, fcpPsi, maaspPsi, maxAllowableMudDensityPpg, FORMULAS_VERSION } from "@/lib/openg/formulas/pressures";
import { strokesForVolume, minutesForStrokes } from "@/lib/openg/formulas/volumes";
import { computeDrillersSchedule, DRILLERS_SCHEDULE_VERSION } from "@/lib/openg/formulas/drillersSchedule";
import { UNITS_VERSION, asNum } from "@/lib/openg/units";

function _num(v) {
  return Number.isFinite(Number(v)) && v !== "" && v != null ? Number(v) : "";
}

/** Re-compute the outputs block for a kill sheet. Returns a NEW outputs object. */
export function computeKillSheetOutputs(rec) {
  const i = rec?.inputs || {};
  const kmw = kmwPpg({
    mwInPpg: i.mwInPpg, sidppPsi: i.sidppPsi, tvdFt: i.currentTvdFt,
    safetyMarginPpg: i.safetyMarginPpg,
  });
  const icp = icpPsi({ sidppPsi: i.sidppPsi, slowPumpPressurePsi: i.slowPumpPressurePsi });
  const fcp = fcpPsi({
    slowPumpPressurePsi: i.slowPumpPressurePsi, kmwPpg: kmw, mwInPpg: i.mwInPpg,
  });

  const q = asNum(i.pumpOutputBblStk);
  const r = asNum(i.pumpRateSpm);

  const sSurfaceToBit = strokesForVolume(i.stringInternalVolBbl, q);
  const sBitToShoe    = strokesForVolume(i.bitToShoeVolBbl, q);
  const sShoeToSurf   = strokesForVolume(i.shoeToSurfaceVolBbl, q);

  const tSurfaceToBit = minutesForStrokes(sSurfaceToBit, r);
  const tBitToShoe    = minutesForStrokes(sBitToShoe, r);
  const tShoeToSurf   = minutesForStrokes(sShoeToSurf, r);

  const notes = _softNotes(i, kmw);

  // Pass D — MAASP / max allowable mud density. Both ride entirely on
  // optional LOT inputs; if either is blank, every MAASP cell stays NaN
  // and the editor renders "—".
  const mamw      = maxAllowableMudDensityPpg({
    lotPressurePsi: i.lotPressurePsi,
    mwAtLotPpg:     i.mwAtLotPpg,
    shoeTvdFt:      i.casingShoeTvdFt,
  });
  const maaspInit = maaspPsi({
    maxMudDensityPpg: mamw,
    currentMwPpg:     i.mwInPpg,
    shoeTvdFt:        i.casingShoeTvdFt,
  });
  const maaspKmw  = maaspPsi({
    maxMudDensityPpg: mamw,
    currentMwPpg:     kmw,
    shoeTvdFt:        i.casingShoeTvdFt,
  });

  // Guardrail breach evaluation — only meaningful when MAMW is known.
  // Each flag captures one explicit operational warning a print layer or
  // editor banner can surface independently.
  const guardrails = _evaluateMaaspGuardrails({
    mamw, kmw, mwIn: i.mwInPpg,
    maaspInit, maaspKmw,
    sicpPsi: asNum(i.sicpPsi), sidppPsi: asNum(i.sidppPsi),
  });
  // Append critical guardrail messages to the notes block so they ride
  // along into UI + print + the calculation trace echo.
  for (const g of guardrails.criticalMessages) notes.push(g);

  // Driller's Schedule — method-pluggable step-down. Pass the kill method
  // from the engineering basis (driller's-method vs wait-and-weight) and
  // the operator-configurable stroke increment from inputs.
  const schedule = computeDrillersSchedule({
    method:           rec?.basis?.killMethod,
    icpPsi:           icp,
    fcpPsi:           fcp,
    strokesToBit:     sSurfaceToBit,
    // Surface → Bit → Shoe → Surface display correction — the same already-
    // computed segment strokes above, threaded through so the schedule can
    // extend past the bit. Never recomputed inside drillersSchedule.js.
    strokesBitToShoe:     sBitToShoe,
    strokesShoeToSurface: sShoeToSurf,
    spm:              r,
    intervalStrokes:  i.scheduleIntervalStrokes,
    kmwPpg:           kmw,
    mwInPpg:          i.mwInPpg,
    tvdFt:            i.currentTvdFt,
    safetyMarginPpg:  i.safetyMarginPpg,
  });

  // Round headline numbers to a sensible precision for display + storage.
  // Don't round here in a way that breaks the audit — store at full
  // precision; the UI formats on read.
  return {
    kmwPpg:                 _num(kmw),
    icpPsi:                 _num(icp),
    fcpPsi:                 _num(fcp),
    strokesSurfaceToBit:    _num(sSurfaceToBit),
    strokesBitToShoe:       _num(sBitToShoe),
    strokesShoeToSurface:   _num(sShoeToSurf),
    // The underlying volumes each stroke/time figure was calculated FROM,
    // carried through at full precision so the UI can show volume + strokes
    // + time side by side without reconstructing a volume from rounded
    // strokes. No new engineering output — these are the sheet's own inputs.
    volStringInternalBbl:   _num(i.stringInternalVolBbl),
    volBitToShoeBbl:        _num(i.bitToShoeVolBbl),
    volShoeToSurfaceBbl:    _num(i.shoeToSurfaceVolBbl),
    volSurfaceLineBbl:      _num(i.surfaceLineVolBbl),
    timeSurfaceToBitMin:    _num(tSurfaceToBit),
    timeBitToShoeMin:       _num(tBitToShoe),
    timeShoeToSurfaceMin:   _num(tShoeToSurf),
    // Pass D — MAASP block.
    maxAllowableMwPpg:      _num(mamw),
    maaspInitialPsi:        _num(maaspInit),
    maaspAtKmwPsi:          _num(maaspKmw),
    guardrails,
    notes,
    // Method-pluggable Driller's Schedule (Pass B). `ok:false` rows means
    // inputs were insufficient — `schedule.notes` carries human messages.
    schedule,
    // Calculation trace — every headline number's formula + the exact inputs
    // it consumed. Rendered by the "Engineering Details / Calculation Trace"
    // panel in the editor and preserved as part of the audit snapshot.
    trace: {
      kmwPpg: {
        formula: "KMW = MW_in + SIDPP / (0.052 × TVD) + safetyMargin",
        inputs: {
          mwInPpg: i.mwInPpg, sidppPsi: i.sidppPsi,
          currentTvdFt: i.currentTvdFt, safetyMarginPpg: i.safetyMarginPpg,
        },
      },
      icpPsi: {
        formula: "ICP = SIDPP + Slow-pump pressure",
        inputs: { sidppPsi: i.sidppPsi, slowPumpPressurePsi: i.slowPumpPressurePsi },
      },
      fcpPsi: {
        formula: "FCP = Slow-pump pressure × (KMW / MW_in)",
        inputs: { slowPumpPressurePsi: i.slowPumpPressurePsi, kmwPpg: _num(kmw), mwInPpg: i.mwInPpg },
      },
      strokesSurfaceToBit: {
        formula: "strokes = string internal vol / pump output",
        inputs: { stringInternalVolBbl: i.stringInternalVolBbl, pumpOutputBblStk: i.pumpOutputBblStk },
      },
      strokesBitToShoe: {
        formula: "strokes = bit→shoe vol / pump output",
        inputs: { bitToShoeVolBbl: i.bitToShoeVolBbl, pumpOutputBblStk: i.pumpOutputBblStk },
      },
      strokesShoeToSurface: {
        formula: "strokes = shoe→surface vol / pump output",
        inputs: { shoeToSurfaceVolBbl: i.shoeToSurfaceVolBbl, pumpOutputBblStk: i.pumpOutputBblStk },
      },
      timeSurfaceToBitMin: {
        formula: "minutes = strokes / SPM",
        inputs: { strokes: _num(sSurfaceToBit), pumpRateSpm: i.pumpRateSpm },
      },
      timeBitToShoeMin: {
        formula: "minutes = strokes / SPM",
        inputs: { strokes: _num(sBitToShoe), pumpRateSpm: i.pumpRateSpm },
      },
      timeShoeToSurfaceMin: {
        formula: "minutes = strokes / SPM",
        inputs: { strokes: _num(sShoeToSurf), pumpRateSpm: i.pumpRateSpm },
      },
      // Pass D — MAASP trace entries.
      maxAllowableMwPpg: {
        formula: "MAMW = (LOT / (0.052 × shoeTVD)) + MW_at_LOT",
        inputs: { lotPressurePsi: i.lotPressurePsi, mwAtLotPpg: i.mwAtLotPpg,
                  casingShoeTvdFt: i.casingShoeTvdFt },
      },
      maaspInitialPsi: {
        formula: "MAASP_initial = (MAMW − MW_in) × 0.052 × shoeTVD",
        inputs: { maxAllowableMwPpg: _num(mamw), mwInPpg: i.mwInPpg,
                  casingShoeTvdFt: i.casingShoeTvdFt },
      },
      maaspAtKmwPsi: {
        formula: "MAASP_at_KMW = (MAMW − KMW) × 0.052 × shoeTVD",
        inputs: { maxAllowableMwPpg: _num(mamw), kmwPpg: _num(kmw),
                  casingShoeTvdFt: i.casingShoeTvdFt },
      },
    },
    provenance: {
      formulasVersion: FORMULAS_VERSION,
      unitsVersion: UNITS_VERSION,
      scheduleVersion: DRILLERS_SCHEDULE_VERSION,
      computedAt: new Date().toISOString(),
    },
  };
}

function _softNotes(i, kmw) {
  const out = [];
  if (Number.isFinite(kmw) && kmw > 18) out.push(`KMW ${kmw.toFixed(2)} ppg is above 18 ppg — verify mud-mix capability.`);
  if (Number.isFinite(kmw) && Number.isFinite(Number(i.mwInPpg)) && Number(kmw) < Number(i.mwInPpg))
    out.push("KMW is below current mud weight — re-check SIDPP / TVD inputs.");
  const sicp = asNum(i.sicpPsi), sidpp = asNum(i.sidppPsi);
  if (Number.isFinite(sicp) && Number.isFinite(sidpp) && sicp < sidpp)
    out.push("SICP < SIDPP — unusual; verify the surface readings.");
  return out;
}

// Evaluate the four operational MAASP guardrails. Each flag is independent
// so the UI can light them up one at a time. `criticalMessages` carries the
// operator-facing strings that get pushed into the main notes array so they
// also flow into the print layout.
function _evaluateMaaspGuardrails({ mamw, kmw, mwIn, maaspInit, maaspKmw, sicpPsi, sidppPsi }) {
  const out = {
    available: Number.isFinite(mamw),
    kmwExceedsMamw:        false,
    mwInExceedsMamw:       false,
    maaspInitialNegative:  false,
    maaspAtKmwNegative:    false,
    sicpExceedsMaasp:      false,
    criticalMessages: [],
  };
  if (!out.available) return out;

  if (Number.isFinite(kmw) && kmw > mamw) {
    out.kmwExceedsMamw = true;
    out.criticalMessages.push(
      `MAASP GUARDRAIL: KMW ${kmw.toFixed(2)} ppg exceeds Max Allowable Mud Weight ${mamw.toFixed(2)} ppg — well cannot be killed by displacement alone without fracturing the casing shoe.`);
  }
  if (Number.isFinite(mwIn) && Number(mwIn) > mamw) {
    out.mwInExceedsMamw = true;
    out.criticalMessages.push(
      `MAASP GUARDRAIL: Current mud weight ${Number(mwIn).toFixed(2)} ppg already exceeds Max Allowable ${mamw.toFixed(2)} ppg — shoe may be at or above fracture pressure.`);
  }
  if (Number.isFinite(maaspInit) && maaspInit <= 0) {
    out.maaspInitialNegative = true;
  }
  if (Number.isFinite(maaspKmw) && maaspKmw <= 0) {
    out.maaspAtKmwNegative = true;
    if (!out.kmwExceedsMamw) {
      out.criticalMessages.push(
        `MAASP GUARDRAIL: MAASP at KMW is ${maaspKmw.toFixed(0)} psi — kill mud will leave zero margin to fracture pressure at the shoe.`);
    }
  }
  if (Number.isFinite(sicpPsi) && Number.isFinite(maaspInit) && sicpPsi > maaspInit) {
    out.sicpExceedsMaasp = true;
    out.criticalMessages.push(
      `MAASP GUARDRAIL: SICP ${Math.round(sicpPsi)} psi exceeds MAASP_initial ${Math.round(maaspInit)} psi — bleed pressure or expect fracture.`);
  }
  return out;
}
